import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import {
  EVENT_LIMIT_MAX,
  EVENT_WINDOW_PRESETS,
  sanitizeEventFilters,
  type EventFilters,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/content-blocks';

import { withoutEmptyValues } from '../utils';
import FieldShell from './FieldShell';
import MultiSelectField, { type MultiSelectOption } from './MultiSelectField';
import SegmentedField from './SegmentedField';

interface EventFiltersFieldProps {
  title?: string;
  description?: string;
  /** The stored filters object (untrusted — sanitized before display). */
  value?: unknown;
  /** Live venue options, injected by the host (like screenOptions). */
  venueOptions?: MultiSelectOption[];
  onChange: (value: EventFilters) => void;
}

/**
 * Sub-form for the EVENTS filter vocabulary (the kit's `EventFilters` —
 * `filterSet: 'events'`; SchemaFields routes the generic `query-filters`
 * format here). Controls map 1:1 to the vocabulary; values the author
 * resets to "all"/empty are REMOVED from the stored object, so it only
 * ever contains real narrowing decisions (and re-saving normalizes
 * unknown keys away).
 */
export default function EventFiltersField({
  title,
  description,
  value,
  venueOptions = [],
  onChange,
}: EventFiltersFieldProps) {
  const filters = sanitizeEventFilters(value);

  const patch = (changes: Partial<Record<keyof EventFilters, unknown>>) => {
    onChange(withoutEmptyValues({ ...filters, ...changes }) as EventFilters);
  };

  // Keep a non-preset stored window (hand-edited content) selectable.
  const windowDays = filters.window?.withinDays;
  const windowChoices: number[] =
    windowDays && !EVENT_WINDOW_PRESETS.includes(windowDays as 30 | 60 | 90)
      ? [...EVENT_WINDOW_PRESETS, windowDays].sort((a, b) => a - b)
      : [...EVENT_WINDOW_PRESETS];

  return (
    <FieldShell title={title} description={description}>
      <div className="space-y-3">
        <MultiSelectField
          title="Venues"
          placeholder="All venues"
          options={venueOptions}
          values={filters.venueIds ?? []}
          onChange={(venueIds) =>
            patch({ venueIds: venueIds.length > 0 ? venueIds : undefined })
          }
        />

        <SegmentedField
          title="Audience"
          options={[
            { value: 'all', label: 'All' },
            { value: 'membersOnly', label: 'Members Only' },
            { value: 'public', label: 'Public' },
          ]}
          value={filters.audience ?? 'all'}
          onChange={(audience) =>
            patch({ audience: audience === 'all' ? undefined : audience })
          }
        />

        <FieldShell title="Date Window">
          <Select
            value={windowDays ? String(windowDays) : 'all'}
            onValueChange={(next) =>
              patch({
                window:
                  next === 'all' ? undefined : { withinDays: Number(next) },
              })
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All upcoming</SelectItem>
              {windowChoices.map((days) => (
                <SelectItem key={days} value={String(days)}>
                  Next {days} days
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FieldShell>

        <SegmentedField
          title="Pricing"
          options={[
            { value: 'all', label: 'All' },
            { value: 'free', label: 'Free' },
            { value: 'ticketed', label: 'Ticketed' },
          ]}
          value={filters.pricing ?? 'all'}
          onChange={(pricing) =>
            patch({ pricing: pricing === 'all' ? undefined : pricing })
          }
        />

        <FieldShell
          title="Max Events"
          description={`Per row, up to ${EVENT_LIMIT_MAX}. Empty shows all.`}
        >
          <Input
            type="number"
            min={1}
            max={EVENT_LIMIT_MAX}
            placeholder="No limit"
            value={filters.limit ?? ''}
            onChange={(e) => {
              const parsed = Number.parseInt(e.target.value, 10);
              patch({
                limit:
                  Number.isNaN(parsed) || parsed < 1
                    ? undefined
                    : Math.min(parsed, EVENT_LIMIT_MAX),
              });
            }}
          />
        </FieldShell>
      </div>
    </FieldShell>
  );
}
